import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addMonths, subMonths } from 'date-fns';
import styled, { keyframes } from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 20px;
  background-color: #f0f4f8;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

const progressAnimation = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

const ProgressBar = styled.div`
  height: 100%;
  background-color: #68D391; /* A pleasant green */
  border-radius: 4px;
  animation: ${progressAnimation} 2s ease-out forwards;
`;

export default function CalendarView() {
    const dispatch = useDispatch();
    const { channelConfig, currentChannel, calendarDate, calendarUpdateFromWebSocket } = useSelector((state) => state.channel);
    const followMode = useSelector((state) => state.channel.followMode);
    const meta = channelConfig[currentChannel] || { title: "Calendar" };
    const calendarConfig = channelConfig['calendar'] || {};
    const temporalDiscombobulationLabel = calendarConfig.temporalDiscombobulationLabel || 'Temporal Discombobulator';

    // Use only the date part from the payload (avoid timezone issues)
    const getDateOnly = (dateString) => {
        if (!dateString) return null;
        // Accepts 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm:ss...' and returns a Date at local midnight
        const [year, month, day] = dateString.split('T')[0].split('-');
        return new Date(Number(year), Number(month) - 1, Number(day));
    };
    // Initialize currentDate from calendarDate if available
    const initialCalendarDate = calendarDate ? getDateOnly(calendarDate) : new Date();
    initialCalendarDate.setHours(0, 0, 0, 0); // Ensure it's also at the start of the day
    const [currentDate, setCurrentDate] = useState(initialCalendarDate);
    const [animationClass, setAnimationClass] = useState('');
    const [highlightedDate, setHighlightedDate] = useState(null);
    const [advancingToDate, setAdvancingToDate] = useState(null);
    const animationTimeoutRef = useRef(null); // Ref to store animation timeout IDs

    const year = currentDate.getFullYear();
    const month = currentDate.toLocaleString('default', { month: 'long' });
    const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const getWeeks = useCallback(() => {
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
        const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
        const weeks = [];
        let week = new Array(firstDayOfMonth).fill(null);

        for (let day = 1; day <= daysInMonth; day++) {
            week.push(day);
            if (week.length === 7) {
                weeks.push(week);
                week = [];
            }
        }
        if (week.length > 0) {
            while (week.length < 7) {
                week.push(null);
            }
            weeks.push(week);
        }
        return weeks;
    }, [currentDate]);

    const weeks = useMemo(() => getWeeks(), [getWeeks]);

    const progressToDate = useCallback((targetDateString) => {
        if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current);
        }
        const targetDate = getDateOnly(targetDateString);
        // Ensure targetDate is at the beginning of the day in local time
        targetDate.setHours(0, 0, 0, 0);
        if (isNaN(targetDate.getTime())) {
            setAnimationClass('');
            setAdvancingToDate(null);
            setHighlightedDate(null);
            return;
        }
        setAnimationClass('animate-progress');
        setAdvancingToDate(targetDate);
        const totalDuration = 10000; // ms
        const minDuration = 5000; // ms, minimum overlay time for small jumps
        const daysToAnimate = Math.abs(Math.round((targetDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)));
        let steps = daysToAnimate;
        if (daysToAnimate > 30) {
            steps = 30; // cap steps for large jumps
        } else if (daysToAnimate < 2) {
            steps = 2; // always animate at least 2 steps for visibility
        }
        const duration = daysToAnimate < 2 ? minDuration : totalDuration;
        const interval = duration / steps;
        let dateIterator = new Date(currentDate);
        const step = targetDate > currentDate ? 1 : -1;
        let currentStep = 0;
        const animateStep = () => {
            if (currentStep < steps - 1) {
                // Interpolate date for large jumps
                if (daysToAnimate > 30) {
                    const progress = (currentStep + 1) / steps;
                    const newTime = currentDate.getTime() + (targetDate.getTime() - currentDate.getTime()) * progress;
                    dateIterator = new Date(newTime);
                } else {
                    dateIterator.setDate(dateIterator.getDate() + step);
                }
                setHighlightedDate(new Date(dateIterator));
                setCurrentDate(new Date(dateIterator));
                currentStep++;
                animationTimeoutRef.current = setTimeout(animateStep, interval);
            } else {
                // Always set to exact target date at the end
                setCurrentDate(targetDate); // <-- ensure calendar grid updates
                setHighlightedDate(targetDate);
                setTimeout(() => setHighlightedDate(null), 300);
                setTimeout(() => {
                    setAnimationClass('');
                    setAdvancingToDate(null);
                }, 350); // hide overlay after highlight
            }
        };
        animateStep();
    }, [currentDate, animationTimeoutRef]);


    useEffect(() => {
        if (
            currentChannel === 'calendar' &&
            calendarDate &&
            calendarUpdateFromWebSocket
        ) {
            progressToDate(calendarDate);
            // Reset the websocket flag so animation only plays once per event
            dispatch({ type: 'channel/resetCalendarUpdateFromWebSocket' });
        }
    }, [calendarDate, currentChannel, calendarUpdateFromWebSocket, dispatch, progressToDate]);

    const handleDisabledClick = () => {
        // Placeholder for future functionality
        console.log("This feature is not yet implemented.");
    };

    if (!calendarDate) {
        return <Container>No event scheduled.</Container>;
    }

    // Disable navigation if followMode is true and not in agent channel
    const navigationDisabled = followMode && currentChannel !== 'agent';

    return (
        <div className="bg-white h-full flex flex-col font-inter relative">
            {/* Header */}
            <div className="flex items-center justify-center p-4 border-b relative">
                <div className="text-center">
                    <h1 className="text-lg font-bold text-gray-800">{meta.title || 'Calendar'}</h1>
                </div>
            </div>

            {/* Calendar Body */}
            <div className={`flex-1 flex flex-col p-4`}>
                <div className="flex items-center justify-between mb-4">
                    <button onClick={navigationDisabled ? handleDisabledClick : () => setCurrentDate(subMonths(currentDate, 1))} className="p-1 rounded-full hover:bg-gray-100" disabled={navigationDisabled}>
                        &lt;
                    </button>
                    <div className="text-center">
                        <div className="text-sm text-gray-500">{year}</div>
                        <div className="text-lg font-semibold">{month}</div>
                    </div>
                    <button onClick={navigationDisabled ? handleDisabledClick : () => setCurrentDate(addMonths(currentDate, 1))} className="p-1 rounded-full hover:bg-gray-100" disabled={navigationDisabled}>
                        &gt;
                    </button>
                </div>

                {/* Days of Week Header */}
                <div className="grid grid-cols-7 text-center text-sm text-gray-500 py-2">
                    {daysOfWeek.map((day, index) => <div key={index}>{day}</div>)}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 text-center text-base">
                    {weeks.flat().map((day, index) => {
                        const currentDayDate = day ? new Date(currentDate.getFullYear(), currentDate.getMonth(), day) : null;
                        const isHighlighted = highlightedDate && currentDayDate && currentDayDate.toDateString() === highlightedDate.toDateString();
                        const isCurrentDate = currentDayDate && currentDate && currentDayDate.toDateString() === currentDate.toDateString();
                        return (
                            <div key={index} className="py-2">
                                {day ? (
                                    <span className={`${(isHighlighted || isCurrentDate) ? 'bg-blue-500 text-white' : ''} rounded-full w-8 h-8 flex items-center justify-center mx-auto`}>
                                        {day}
                                    </span>
                                ) : ''}
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Animation Overlay: Advancing in time */}
            {animationClass && advancingToDate && (
                <div className="absolute inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-50 p-4">
                    <div className="bg-gray-100 rounded-lg p-6 shadow-md flex flex-col items-center">
                        <div className="text-gray-500 text-base mb-2 font-semibold">{temporalDiscombobulationLabel}</div>
                        <div className="text-2xl font-bold text-gray-800">
                            {advancingToDate.toLocaleDateString(undefined, { year: '2-digit', month: '2-digit', day: '2-digit' })}
                        </div>
                        <div className="h-2.5 bg-gray-200 rounded-full w-40 mt-4">
                            <ProgressBar />
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="border-t flex justify-around items-center py-2 text-blue-500">
                <button onClick={handleDisabledClick} className="flex flex-col items-center">
                    <span className="text-lg font-semibold">Calendars</span>
                    <span className="text-xs text-gray-500">All</span>
                </button>
            </div>
        </div>
    );
}